import React from "react";
import GameIcon, { GameIconName } from "./GameIcon";

/**
 * Playful, tactile UI kit for the athlete "game" surface. Chunky rounded
 * shapes with a soft 3px depth (a physical "press"), in the platform's
 * tone (indigo/blue primary, emerald success, amber points, violet
 * badges). Reused across the redesigned /app screens.
 */

// ── Button ─────────────────────────────────────────────────────────
type BtnVariant = "primary" | "mint" | "ghost" | "danger" | "amber";
const BTN: Record<BtnVariant, string> = {
  primary: "text-white bg-gradient-to-b from-indigo-500 to-blue-600 shadow-[0_4px_0_#3730a3] active:shadow-[0_1px_0_#3730a3]",
  mint: "text-white bg-gradient-to-b from-emerald-400 to-emerald-500 shadow-[0_4px_0_#059669] active:shadow-[0_1px_0_#059669]",
  amber: "text-white bg-gradient-to-b from-amber-400 to-amber-500 shadow-[0_4px_0_#b45309] active:shadow-[0_1px_0_#b45309]",
  danger: "text-white bg-gradient-to-b from-rose-500 to-red-500 shadow-[0_4px_0_#be123c] active:shadow-[0_1px_0_#be123c]",
  ghost: "text-indigo-700 bg-white border border-slate-200 shadow-[0_3px_0_#dbe3f0] active:shadow-[0_1px_0_#dbe3f0]",
};
export function GameButton({
  variant = "primary",
  className = "",
  children,
  ...rest
}: { variant?: BtnVariant } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`w-full rounded-2xl px-5 py-3.5 font-extrabold text-[15px] flex items-center justify-center gap-2 transition-transform duration-75 active:translate-y-[3px] disabled:opacity-45 disabled:saturate-50 ${BTN[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Card ───────────────────────────────────────────────────────────
export function GameCard({
  className = "",
  depth = true,
  children,
  ...rest
}: { depth?: boolean } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`bg-white rounded-2xl border border-slate-100 ${depth ? "shadow-[0_3px_0_#eef2f8]" : "shadow-sm"} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

// ── Hero (gradient header) ─────────────────────────────────────────
// `flow` makes the banner spill smoothly out of the dark app header:
// the gradient starts at the header's bottom colour (#0b1120) and eases
// into indigo, with no decorative blobs — for a compact, seamless look.
export function GameHero({
  className = "",
  flow = false,
  children,
}: { className?: string; flow?: boolean; children: React.ReactNode }) {
  const grad = flow
    ? "bg-gradient-to-b from-[#0b1120] via-indigo-800 to-indigo-500"
    : "bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-600";
  return (
    <div className={`relative overflow-hidden ${grad} text-white ${className}`}>
      {!flow && (
        <>
          {/* playful blobs */}
          <div className="pointer-events-none absolute -top-8 -right-6 w-32 h-32 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-10 -left-4 w-28 h-28 rounded-full bg-white/10" />
        </>
      )}
      <div className="relative">{children}</div>
    </div>
  );
}

// ── HUD chip (points / streak / etc.) ──────────────────────────────
export function HudChip({
  icon,
  value,
  tone = "ink",
  className = "",
}: { icon: GameIconName; value: React.ReactNode; tone?: "ink" | "amber" | "orange"; className?: string }) {
  const color = tone === "amber" ? "text-amber-700" : tone === "orange" ? "text-orange-600" : "text-slate-800";
  return (
    <span className={`inline-flex items-center gap-1.5 bg-white rounded-2xl px-2.5 py-1.5 font-extrabold text-sm shadow-[0_2px_0_#e2e8f0] ${color} ${className}`}>
      <GameIcon name={icon} className="w-4 h-4" />
      {value}
    </span>
  );
}

// ── Hearts (lives) ─────────────────────────────────────────────────
// Scales from the classic heart row (≤5, the free/regenerating pool) to a
// compact "heart ×N" once the athlete holds a purchased stockpile, so large
// wallets (up to the 60 cap) stay legible everywhere.
// `max` is the athlete's own regen cap, which is 3 on the free plan and 5 on a
// paid one — not a constant. Hardcoding 5 drew a free athlete with two lives
// left as two full hearts beside three empty ones, which reads as "you are
// missing three" when they are in fact one short of full.
export function LivesBadge({
  lives,
  max = 5,
  className = "",
}: {
  lives: number;
  max?: number;
  className?: string;
}) {
  if (lives <= max) return <Hearts lives={lives} max={max} className={className} />;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <GameIcon name="heart" className="w-5 h-5" />
      <span className="font-extrabold tabular-nums leading-none">×{lives}</span>
    </span>
  );
}

export function Hearts({ lives, max = 3, className = "" }: { lives: number; max?: number; className?: string }) {
  return (
    <span className={`inline-flex gap-0.5 ${className}`}>
      {Array.from({ length: max }).map((_, i) => (
        <GameIcon
          key={i}
          name="heart"
          className={`w-5 h-5 transition-all ${i < lives ? "" : "opacity-25 grayscale"}`}
        />
      ))}
    </span>
  );
}

// ── Segmented control ──────────────────────────────────────────────
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className = "",
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  // Four or more segments do not fit a 360px phone at the roomy padding, and
  // the row either overflows or the last option is clipped off screen. Past
  // three, the segments share the width equally and the padding tightens.
  const crowded = options.length > 3;
  return (
    <div className={`inline-flex bg-slate-100 rounded-2xl p-1 gap-1 ${className}`}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`py-1.5 rounded-xl text-xs font-bold transition-colors ${
            crowded ? "flex-1 min-w-0 px-1.5 truncate" : "px-4"
          } ${value === o.value ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Stat tile ──────────────────────────────────────────────────────
// Compact stat card: icon sits IN FRONT of the value on the first row,
// label on the second — reads tighter and more refined than a tall
// icon-over-number-over-label stack.
export function StatTile({
  icon,
  label,
  value,
  color = "#4f46e5",
  onClick,
}: { icon?: GameIconName; label: string; value: React.ReactNode; color?: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-2xl border border-slate-100 px-3 py-3 shadow-[0_3px_0_#eef2f8] transition-transform ${onClick ? "active:translate-y-[2px]" : "cursor-default"}`}
    >
      {/* Icon sits on its own row; the value then gets the FULL tile width on
          the next line so large numbers (1.000+, formatted with a thousands
          separator) are never squeezed next to the icon and truncated. */}
      {icon && (
        <div className="w-8 h-8 rounded-xl grid place-items-center mb-2" style={{ backgroundColor: `${color}1a` }}>
          <GameIcon name={icon} className="w-[18px] h-[18px]" />
        </div>
      )}
      <div className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none tracking-tight">{value}</div>
      <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wide mt-1.5 truncate">{label}</div>
    </button>
  );
}

// ── Skeleton (loading placeholder) ─────────────────────────────────
// A soft, pulsing block used to reserve layout while data loads, so
// screens fade in gracefully instead of flashing empty then popping.
export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-slate-200/70 ${className}`} />;
}

// ── Circular progress ring (timer + accuracy) ──────────────────────
export function RingProgress({
  pct,
  size = 56,
  stroke = 6,
  color = "#10b981",
  track = "#e2e9f1",
  children,
  className = "",
}: {
  pct: number; // 0..100
  size?: number;
  stroke?: number;
  color?: string;
  track?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset .5s cubic-bezier(.2,.8,.2,1), stroke .3s" }}
        />
      </svg>
      {children && <div className="absolute inset-0 grid place-items-center">{children}</div>}
    </div>
  );
}

// ── Level chip + XP bar ────────────────────────────────────────────
export function LevelBar({
  level,
  pct,
  title,
  xpInto,
  xpFor,
  compact = false,
  className = "",
}: {
  level: number;
  pct: number;
  title?: string;
  xpInto?: number;
  xpFor?: number;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <span className="w-7 h-7 rounded-xl grid place-items-center font-extrabold text-xs text-white bg-gradient-to-br from-indigo-500 to-blue-600 shadow-[0_2px_0_#3730a3]">
        {level}
      </span>
      <div className={compact ? "w-24" : "flex-1 min-w-[120px]"}>
        <div className="h-2 bg-black/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
        </div>
        {!compact && (
          <div className="text-[10px] font-bold opacity-80 mt-0.5">
            {title ? `${title} · ` : ""}
            {xpInto != null && xpFor != null ? `${xpInto}/${xpFor} XP` : `Nivel ${level}`}
          </div>
        )}
      </div>
    </div>
  );
}
